import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { Button } from '@/components/ui/button';
import { PageBody, PageHeader, PageShell } from '@/components/ui/page-shell';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Plus } from 'lucide-react';
import {
  MatterFormDialog,
  MattersList,
} from '@/components/matters';
import api from '@/lib/api';
import { Matter } from '@/lib/store';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import { useUIConfig } from '@/config/ui-config';

// Interface for paginated API response
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Main Matters Page ──────────────────────────────────────────────────────────

export function Matters() {
  const { fetchClients, fetchUsers } = useStore();
  const { toast } = useToast();
  const { matter: matterConfig } = useUIConfig();
  const navigate = useNavigate();

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(matterConfig.defaultShowInactive);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);

  const [matters, setMatters] = useState<Matter[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load every matter so search runs over the full set, not just one page.
  const fetchAllMatters = useCallback(async () => {
    setIsLoading(true);
    try {
      const PAGE = 1000;
      const first = await api.get(`${ROUTES.API.MATTERS}?limit=${PAGE}&offset=0`);
      const data = first.data as PaginatedResponse<Matter>;
      let all = data.results;
      // More than one page? Pull only the remainder, not everything again.
      if (data.count > all.length) {
        const rest = await api.get(`${ROUTES.API.MATTERS}?limit=${data.count - all.length}&offset=${all.length}`);
        all = all.concat((rest.data as PaginatedResponse<Matter>).results);
      }
      setMatters(all);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch matters',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Initialize data — the page renders from local matters state; the store
  // only feeds clients/users to MattersList and the edit dialog
  useEffect(() => {
    fetchClients();
    fetchUsers();
    fetchAllMatters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dialog handlers
  const handleEdit = (matter: Matter) => {
    setSelectedMatter(matter);
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setSelectedMatter(null);
    setIsDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    fetchAllMatters();
  };

  const handleViewDetail = (matter: Matter) => {
    navigate(ROUTES.MATTER_DETAIL.replace(':id', String(matter.id)));
  };

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Matters"
          description="Manage your legal matters, client assignments, and fee sharing."
          actions={
            <Button onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Add matter
            </Button>
          }
        />
        <PageBody>

        <Card>
          <CardContent className="pt-6">
            <MattersList
              matters={matters}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              showInactive={showInactive}
              onShowInactiveChange={setShowInactive}
              onEdit={handleEdit}
              onRefresh={fetchAllMatters}
              isLoading={isLoading}
              onViewDetail={handleViewDetail}
            />
          </CardContent>
        </Card>

        <MatterFormDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          matter={selectedMatter}
          onSuccess={handleDialogSuccess}
        />
        </PageBody>
      </PageShell>
    </Layout>
  );
}